import { type ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Form, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from '@/components/ModalSheet'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import styles from './ManageSkillsDialog.module.css'
import { type ManageSkillsDialogTab } from '@/features/user-management/ManageSkillsDialog/ManageSkillsDialogViewModel'

export interface ManageSkillsDialogProps {
  showing: boolean
  onClose: () => void
  /**
   * The single user being edited, or `null` when editing a selection.
   */
  selectedUserName: string | null
  /**
   * The workspace the skills apply to, or `null` when one must be picked.
   */
  selectedWorkspaceName: string | null
  selectedTab: ManageSkillsDialogTab
  onTabChange: (tab: ManageSkillsDialogTab) => void
  onSubmit: () => void
  isSubmitPending: boolean
  /**
   * The workspace and skills fields.
   */
  children: ReactNode
}

export function ManageSkillsDialog({
  showing,
  onClose,
  selectedUserName,
  selectedWorkspaceName,
  selectedTab,
  onTabChange,
  onSubmit,
  isSubmitPending,
  children,
}: ManageSkillsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetSection horizontallySpaced>
          <ModalSheetContentTitle>Manage skills</ModalSheetContentTitle>
          <ModalSheetContentDescription compact>
            Add or remove skills from{' '}
            {selectedUserName !== null ? (
              <span className={styles.headerName}>{selectedUserName}</span>
            ) : (
              'the selected users'
            )}
            {selectedWorkspaceName !== null && (
              <>
                {' in '}
                <span className={styles.headerName}>
                  {selectedWorkspaceName}
                </span>
              </>
            )}
            .
          </ModalSheetContentDescription>
        </ModalSheetSection>

        <ModalSheetSection spaced>
          <Form id="manageSkills" onSubmit={onSubmit}>
            <FormRow>
              <FormGroupContainer>
                <RadioButtonGroup>
                  <RadioButtonItem
                    className={styles.tabButton}
                    onPress={() => onTabChange('Add')}
                    active={selectedTab === 'Add'}
                    isDisabled={isSubmitPending}
                    testId="manage-skills-tab-add"
                  >
                    Add
                  </RadioButtonItem>
                  <RadioButtonItem
                    className={styles.tabButton}
                    onPress={() => onTabChange('Remove')}
                    active={selectedTab === 'Remove'}
                    isDisabled={isSubmitPending}
                    testId="manage-skills-tab-remove"
                  >
                    Remove
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroupContainer>
            </FormRow>

            {children}
          </Form>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label="Save"
            type="submit"
            form="manageSkills"
            variant="primary"
            isDisabled={isSubmitPending}
            isPending={isSubmitPending}
            testId="manage-skills-submit"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
